import { t } from "@lingui/macro";
import {
  NavigationState,
  useIsFocused,
  useLinkTo,
  useNavigation,
  useNavigationState,
} from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import * as Sentry from "@sentry/react";
import * as Linking from "expo-linking";
import { useAtom } from "jotai";
import React, { useLayoutEffect, useRef } from "react";
import { Platform } from "react-native";

import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import { getStoredClubSlug } from "@whoosh/relay/src";

import { preAuthenticatedPath_atom } from "../../features/Authentication/atoms";
import { useHandleLogOutCleanUp } from "../../features/Authentication/hooks/useHandleLogOutCleanUp";
import { executePostLoginRedirect } from "../../features/Authentication/utils/executePostLoginRedirect";
import { resolvePostLoginRedirect } from "../../features/Authentication/utils/resolvePostLoginRedirect";
import { hasAdminRequestErrorAtom } from "../../features/Session";
import { BaseNavigatorParamList } from "../../navigators/BaseNavigator/types";
import { UnauthenticatedStackNavigator } from "../../navigators/UnauthenticatedStackNavigator";
import { useMutationWithError } from "../../utils/hooks/useMutationWithError";
import { LogOutMutationString } from "../Profile/mutations/LogOut";

type SessionClub = {
  slug: string;
};

type UnauthenticatedScreenProps = {
  hasSession: boolean;
  userClubs: SessionClub[];
};

const TOKENIZED_UNAUTHENTICATED_ROUTE_PATTERNS = [
  /^\/patron\/reset-password\/[^/?#]+$/,
  /^\/patron\/register\/[^/?#]+$/,
];

function normalizeRoutePath(value: null | string | undefined): null | string {
  if (!value) {
    return null;
  }

  if (value.includes("://")) {
    try {
      const url = new URL(value);
      const pathname = url.pathname.replace(/^\/--/, "");

      if (url.protocol === "whoosh:" && url.host) {
        return `/${url.host}${pathname}`;
      }

      return pathname;
    } catch {
      return null;
    }
  }

  if (!value.startsWith("/")) {
    return null;
  }

  return value.split(/[?#]/)[0];
}

function getActiveNestedRouteName(state: NavigationState): null | string {
  let nestedState = state.routes[state.index]?.state;
  let routeName: null | string = null;

  while (nestedState) {
    const route = nestedState.routes[nestedState.index ?? 0];

    if (!route) {
      return routeName;
    }

    routeName = route.name;
    nestedState = route.state;
  }

  return routeName;
}

function isTokenizedUnauthenticatedRoute(path: null | string): boolean {
  if (!path) {
    return false;
  }

  return TOKENIZED_UNAUTHENTICATED_ROUTE_PATTERNS.some((pattern) =>
    pattern.test(path)
  );
}

export const UnauthenticatedScreen = ({
  hasSession,
  userClubs,
}: UnauthenticatedScreenProps) => {
  const hasMemberSession = hasSession && userClubs.length > 0;
  const { showToast } = useToast();
  const navigation =
    useNavigation<NativeStackNavigationProp<BaseNavigatorParamList>>();
  const linkTo = useLinkTo();
  const [preAuthenticatedPath, setPreAuthenticatedPath] = useAtom(
    preAuthenticatedPath_atom
  );
  const handleLogOutCleanup = useHandleLogOutCleanUp();
  const [tryLogOut] = useMutationWithError(LogOutMutationString);

  const [adminRequestError, setAdminRequestError] = useAtom(
    hasAdminRequestErrorAtom
  );
  const hasHandledRedirect = useRef(false);
  const redirectAttemptRef = useRef(0);
  const isFocused = useIsFocused();
  const currentUrl = Linking.useURL();
  const activeNestedRouteName = useNavigationState(getActiveNestedRouteName);
  const webPathname =
    Platform.OS === "web" ? normalizeRoutePath(window.location.pathname) : null;
  const preAuthenticatedRoutePath = normalizeRoutePath(preAuthenticatedPath);
  const linkedRoutePath =
    activeNestedRouteName === null ? normalizeRoutePath(currentUrl) : null;
  const isActiveTokenizedRoute =
    activeNestedRouteName === "ResetPassword" ||
    activeNestedRouteName === "Register";
  const shouldBypassExistingSessionRedirect =
    isTokenizedUnauthenticatedRoute(webPathname) ||
    isTokenizedUnauthenticatedRoute(preAuthenticatedRoutePath) ||
    isTokenizedUnauthenticatedRoute(linkedRoutePath) ||
    isActiveTokenizedRoute;
  const shouldAutoRedirectMemberSession =
    hasMemberSession && !shouldBypassExistingSessionRedirect;

  useLayoutEffect(() => {
    return navigation.addListener("blur", () => {
      redirectAttemptRef.current += 1;
      hasHandledRedirect.current = false;
    });
  }, [navigation]);

  useLayoutEffect(() => {
    let isCancelled = false;

    // If we are trying to access this area when we are logged in redirect to first club
    // in the users clubs
    async function redirect() {
      try {
        if (
          !isFocused ||
          hasHandledRedirect.current ||
          (!adminRequestError && !shouldAutoRedirectMemberSession)
        ) {
          return;
        }

        // Mark this focus cycle as handled before awaiting storage or navigating.
        // Native screens are frozen on blur, so the navigation blur event above
        // resets this guard without relying on a render of the unfocused screen.
        hasHandledRedirect.current = true;
        const redirectAttempt = redirectAttemptRef.current + 1;
        redirectAttemptRef.current = redirectAttempt;

        if (!adminRequestError) {
          /**
           * We attempt to redirect you to the last club you were in -- in the case of a cold boot.
           *
           * This could be considerably improved but would be a larger effort that would require untangling the session
           * atoms.
           *
           */
          let storedSlug = await getStoredClubSlug();

          if (isCancelled || redirectAttempt !== redirectAttemptRef.current) {
            return;
          }

          //We confirm that our stored slug exists in the user's clubs
          if (!userClubs.find((uc) => uc.slug === storedSlug)) {
            storedSlug = null;
          }
          const defaultClubSlug = storedSlug || userClubs?.[0]?.slug || null;
          if (Platform.OS === "web") {
            const redirectPath = resolvePostLoginRedirect({
              locationSearch: window.location.search,
              preAuthenticatedPath,
            });
            const didRedirect = executePostLoginRedirect({
              clearPreAuthenticatedPath: () => setPreAuthenticatedPath(null),
              redirectExternal: (url) => {
                if (Platform.OS === "web") {
                  window.location.href = url;
                  return;
                }

                navigation.navigate("Club", {
                  clubSlug: defaultClubSlug ?? "",
                  screen: "Home",
                  params: {
                    screen: "WebView",
                    params: {
                      title: t`Continue`,
                      url,
                    },
                  },
                });
              },
              redirectInApp: (path) => {
                linkTo(path);
              },
              redirectPath,
            });
            if (didRedirect) {
              return;
            }
            if (defaultClubSlug) {
              window.location.href = `/patron/club/${defaultClubSlug}`;
            }
          } else {
            const didRedirect = executePostLoginRedirect({
              clearPreAuthenticatedPath: () => setPreAuthenticatedPath(null),
              redirectExternal: (url) => {
                navigation.navigate("Club", {
                  clubSlug: defaultClubSlug ?? "",
                  screen: "Home",
                  params: {
                    screen: "WebView",
                    params: {
                      title: t`Continue`,
                      url,
                    },
                  },
                });
              },
              redirectInApp: (path) => {
                linkTo(path);
              },
              redirectPath: preAuthenticatedPath,
            });
            if (didRedirect) {
              return;
            }
            if (defaultClubSlug) {
              navigation.navigate("Club", {
                clubSlug: defaultClubSlug,
                screen: "Home",
                params: {
                  clubSlug: defaultClubSlug,
                  screen: "MainHome",
                },
              });
            }
          }
        } else {
          tryLogOut({
            variables: { input: {} },
            onCompleted: () => {
              setAdminRequestError(false);
              handleLogOutCleanup();
            },
            onPayloadError: (error) => {},
          });

          showToast({
            description: t`Whoops! For now, only members can log into the mobile application.`,
            semanticStyle: "error",
            toastId: "staff-login-error",
          });
        }
      } catch (e) {
        Sentry.captureException(e);
      }
    }

    redirect();

    return () => {
      isCancelled = true;
    };
  }, [
    navigation,
    linkTo,
    preAuthenticatedPath,
    setPreAuthenticatedPath,
    userClubs,
    hasMemberSession,
    shouldAutoRedirectMemberSession,
    isFocused,
    adminRequestError,
    handleLogOutCleanup,
    showToast,
    tryLogOut,
    setAdminRequestError,
  ]);

  return (
    <UnauthenticatedStackNavigator
      defaultClubSlug={userClubs?.[0]?.slug ?? null}
      hasSession={shouldAutoRedirectMemberSession}
    />
  );
};
